import { useQuery } from '@tanstack/react-query'

import { useDebounce } from '@uidotdev/usehooks'

import { AnalyticsTagType, GetProductListViewModel } from '@/types/api'

import { useDrawer } from '@/features/drawer/context/use-drawer'
import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider'
import { useUserToken } from '@/features/user-token-provider/use-user-token'

import { populateSearchParamsFromBasicMetas } from '@/app/(plp)/_utils/populate-search-params-from-basic-metas'
import { useBasicMetas } from '@/app/(plp)/_utils/use-basic-metas'
import { usePLPFacetsContext } from '@/app/(plp)/facets-context/facets-context'
import { PUBLIC_IS_BOT } from '@/app/_constants/search-engines'
import fetchRequest from '@/app/_utils/fetch-request'
import { QueryKeys } from '@/app/_utils/react-query'

import { generateSearchParams } from '@/utils/generate-search-params'
import { searchParamsToObject } from '@/utils/search-params-to-object'

import { MobileFacetDrawerName } from '../constants'

export function useProductsCount() {
    const { isDrawerOpen } = useDrawer()
    const isMobileFacetsOpen = isDrawerOpen(MobileFacetDrawerName)
    const { isMobile } = useMobileDetect()
    const { userToken } = useUserToken()
    const { basicMetas } = useBasicMetas()
    const { facetsSearchParams, page } = usePLPFacetsContext()

    const facetSearchParamsObj = searchParamsToObject(facetsSearchParams)

    const populatedSearchParams = populateSearchParamsFromBasicMetas({
        page,
        basicMetas,
        searchParams: facetSearchParamsObj,
    })

    if (!PUBLIC_IS_BOT) {
        populatedSearchParams.ut = userToken
    }

    populatedSearchParams.analytics_tag = AnalyticsTagType.CountPLP

    const requestParams = generateSearchParams(populatedSearchParams)

    const stringifiedParams = useDebounce(requestParams.toString(), 200)

    const enabled = isMobile && isMobileFacetsOpen
    const { data, isLoading } = useQuery<GetProductListViewModel>({
        queryKey: QueryKeys.PLP.ProductsCount(stringifiedParams),
        queryFn: () => {
            const endpoint = `/products/count?${stringifiedParams}`
            return fetchRequest({
                endpoint,
                serviceName: 'ntl',
                appendCurrentQueryStrings: false,
                method: 'GET',
            })
        },
        staleTime: Infinity,
        enabled,
    })

    return {
        productsCount: data?.products?.totalCount || 0,
        isLoadingProductsCount: isLoading,
    }
}
